// One compact order attribute; no customer note or line-item properties.
export const ATTRIBUTE = 'Black Friday Landing';
export const TTL = 30 * 60 * 1000;
const KEY = 'momirov:bf-landing:2026:v1';
export function active(state, now) {
  return state?.v === 1 && Number.isFinite(state.at) && now >= state.at && now - state.at < TTL;
}
export function transition(previous, action, now = Date.now()) {
  let state = active(previous, now) ? { ...previous } : null;
  if (!state && action !== 'landing') return null;
  state ||= { v: 1, at: now, site: false, returned: false, added: false, upsell: false, category: false, checkout: '' };
  state.at = now;
  if (action === 'landing' && state.site) state.returned = true;
  if (action === 'site' || action === 'category') state.site = true;
  if (action === 'category') state.category = true;
  if (action === 'added') state.added = true;
  if (action === 'upsell') { state.added = true; state.upsell = true; }
  if (action === 'checkout-landing') state.checkout = 'landing';
  if (action === 'checkout-site') state.checkout = 'site';
  if (action === 'landing' || action === 'site' || action === 'category') state.checkout = '';
  return state;
}
export function summary(state) {
  if (!state) return '';
  const route = state.returned ? 'Landing → site → landing' : state.site ? 'Landing → site' : 'Landing direct';
  return `${route} · Coș BF: ${state.added ? 'da' : 'nu'} · Upsell coș BF: ${state.upsell ? 'da' : 'nu'} · Vezi categoria: ${state.category ? 'da' : 'nu'} · Checkout: ${state.checkout || 'nepornit'}`;
}

export function install(config) {
  if (window.__momirovBFJourney || window.Shopify?.designMode) return;
  window.__momirovBFJourney = true;
  const landing = config.dataset.landing === 'true';
  const root = config.dataset.root || '/';
  let state = null, persisted = config.dataset.existing || '', queue = Promise.resolve(), timer;
  let enabled = false, checkoutPending = false, lastActivity = 0;
  const read = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return state; } };
  const save = () => { try { state ? localStorage.setItem(KEY, JSON.stringify(state)) : localStorage.removeItem(KEY); } catch { /* In-memory fallback. */ } };
  const publish = (name) => {
    if (!enabled || !state) return;
    try { window.Shopify?.analytics?.publish('momirov_bf_' + name, { journey: state.returned ? 'returned' : state.site ? 'site' : 'direct', added_from_landing: state.added, upsell_from_landing_cart: !!state.upsell, category_click: state.category, checkout_from: state.checkout || null }); } catch { /* Analytics never blocks the cart. */ }
  };
  const sync = () => {
    const value = summary(state);
    queue = queue.catch(() => {}).then(async () => {
      if (value === persisted) return;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 1800);
      try {
        const response = await fetch(root + 'cart/update.js', { method: 'POST', credentials: 'same-origin', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: { [ATTRIBUTE]: value } }) });
        if (response.ok) persisted = value;
      } catch { /* Checkout must remain usable on network failure. */ }
      finally { clearTimeout(timeout); }
    });
    return queue;
  };
  const change = (action, eventName) => {
    if (!enabled) return;
    state = transition(read(), action); save();
    if (eventName) publish(eventName);
    clearTimeout(timer); timer = setTimeout(sync, 100);
  };
  const consent = () => {
    const privacy = window.Shopify?.customerPrivacy;
    return !!privacy && privacy.analyticsProcessingAllowed();
  };
  const start = () => {
    const permitted = consent();
    if (!permitted) {
      enabled = false; state = null; save(); clearTimeout(timer);
      if (persisted) sync();
      return;
    }
    if (enabled) return;
    enabled = true;
    const old = read();
    state = transition(old, landing ? 'landing' : 'site'); save();
    if (state) publish(landing ? (active(old, Date.now()) && old.site ? 'return' : 'landing_view') : 'site_visit');
    sync();
  };
  // Track successful additions from both BF purchase flows in their shared cart API.
  document.addEventListener('momirov:bf-added', () => change('added', 'add_to_cart'));
  document.addEventListener('momirov:bf-upsell-added', () => { if (landing) change('upsell', 'upsell_add_to_cart'); });
  const prepareCheckout = () => {
    change(landing ? 'checkout-landing' : 'checkout-site', 'checkout_click');
    clearTimeout(timer);
    return Promise.race([sync(), new Promise(resolve => setTimeout(resolve, 2000))]);
  };
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link || !enabled) return;
    let url; try { url = new URL(link.href, location.href); } catch { return; }
    if (url.origin !== location.origin) return;
    if (/\/(checkouts?)(\/|$)/.test(url.pathname)) {
      if (!state && !persisted) return;
      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || link.target === '_blank') { prepareCheckout(); return; }
      event.preventDefault(); event.stopImmediatePropagation();
      if (checkoutPending) return; checkoutPending = true;
      prepareCheckout().finally(() => { location.assign(url.href); });
      return;
    }
    if (!landing || (url.pathname === location.pathname && url.search === location.search) || /\/pages\/black-friday-2026\/?$/.test(url.pathname)) return;
    change(link.classList.contains('bf-card-link') ? 'category' : 'site', link.classList.contains('bf-card-link') ? 'category_click' : 'exit');
    // Persist before navigation where possible; destination page also reconciles state.
    clearTimeout(timer); sync();
  }, true);
  const resumedForms = new WeakSet();
  document.addEventListener('submit', event => {
    const form = event.target;
    if (resumedForms.has(form)) { resumedForms.delete(form); return; }
    const checkout = event.submitter?.name === 'checkout' || /\/checkouts?(\/|$)/.test(new URL(form.action, location.href).pathname);
    if (!checkout || !enabled || (!state && !persisted)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (checkoutPending) return; checkoutPending = true;
    prepareCheckout().finally(() => {
      checkoutPending = false; resumedForms.add(form);
      form.requestSubmit(event.submitter || undefined);
    });
  }, true);
  // Activity refreshes session expiry, but does not send scroll/click analytics.
  const activity = () => {
    if (!enabled || Date.now() - lastActivity < 15000) return;
    lastActivity = Date.now();
    const old = read();
    if (!active(old, Date.now())) {
      state = transition(null, landing ? 'landing' : 'site'); save(); sync();
    } else { state = { ...old, at: Date.now() }; save(); }
  };
  for (const name of ['pointerdown', 'keydown', 'scroll']) document.addEventListener(name, activity, { passive: true });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) activity(); });
  window.addEventListener('pageshow', event => { if (event.persisted) { checkoutPending = false; activity(); } });
  document.addEventListener('visitorConsentCollected', start);
  if (window.Shopify?.customerPrivacy) start();
  else if (window.Shopify?.loadFeatures) window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) start(); });
}
if (typeof document !== 'undefined') {
  const config = document.querySelector('[data-momirov-bf-journey]');
  if (config) install(config);
}
